import { useEffect, useState } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { signInWithApple, signInWithGoogle } from "@/lib/auth";
import { isAndroid } from "@/lib/nativeAuth";
import { trackEvent, setSignupTrigger } from "@/lib/analytics";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestHandle: string | null;
  searchCount: number;
  /** Called after a successful auth from the paywall. */
  onSignupSuccess: () => void;
  /** Open the existing InlineSignupSheet email flow. */
  onEmailSignup: () => void;
}

const CORAL = "#C84B2F";
const CORAL_BG = "var(--l2f-coral)";
const INK = "#2C2820";
const BODY = "#4A4540";

export default function GuestLimitPaywall({
  open,
  onOpenChange,
  guestHandle,
  searchCount,
  onSignupSuccess,
  onEmailSignup,
}: Props) {
  const [busy, setBusy] = useState<"google" | "apple" | null>(null);
  const [error, setError] = useState<string | null>(null);
  useShownGuard(open, guestHandle, searchCount);

  useEffect(() => {
    if (!open) {
      setBusy(null);
      setError(null);
    }
  }, [open]);

  const fireSignupStarted = (method: "google" | "apple" | "email") => {
    trackEvent("guest_paywall_signup_started", {
      guest_handle: guestHandle,
      search_count: searchCount,
      auth_method: method,
    });
  };

  const handleGoogle = async () => {
    setError(null);
    setBusy("google");
    fireSignupStarted("google");
    setSignupTrigger("guest_search_limit");
    const result = await signInWithGoogle();
    if (result.error) {
      setError(result.error.message ?? "Google sign-in failed");
      setBusy(null);
      return;
    }
    if (!result.redirected) {
      trackEvent("guest_paywall_signup_completed", {
        guest_handle: guestHandle,
        search_count: searchCount,
        auth_method: "google",
      });
      onSignupSuccess();
      onOpenChange(false);
    }
    setBusy(null);
  };

  const handleApple = async () => {
    setError(null);
    setBusy("apple");
    fireSignupStarted("apple");
    setSignupTrigger("guest_search_limit");
    const result = await signInWithApple();
    if (result.error) {
      setError(result.error.message ?? "Apple sign-in failed");
      setBusy(null);
      return;
    }
    if (!result.redirected) {
      trackEvent("guest_paywall_signup_completed", {
        guest_handle: guestHandle,
        search_count: searchCount,
        auth_method: "apple",
      });
      onSignupSuccess();
      onOpenChange(false);
    }
    setBusy(null);
  };

  const handleEmail = () => {
    fireSignupStarted("email");
    onEmailSignup();
  };

  const handleClose = (next: boolean) => {
    if (!next && open) {
      trackEvent("guest_paywall_dismissed", {
        guest_handle: guestHandle,
        search_count: searchCount,
      });
    }
    onOpenChange(next);
  };

  const handle = guestHandle ?? "Friend";

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={handleClose}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className="fixed inset-0 z-[60]"
          style={{ background: "rgba(44,40,32,0.4)" }}
        />
        <DrawerPrimitive.Content
          className="fixed inset-x-0 bottom-0 z-[61] flex flex-col"
          style={{
            background: "#FFFCF8",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            paddingBottom: "calc(16px + var(--safe-bottom))",
            outline: "none",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 36,
              height: 4,
              borderRadius: 999,
              background: "#E0D8D0",
              margin: "8px auto",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              padding: "0 16px 4px",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="l2f-type-section-header"
                style={{ color: CORAL }}
              >
                FREE LIMIT REACHED
              </div>
              <DrawerPrimitive.Title
                style={{
                  fontSize: 20,
                  fontStyle: "italic",
                  fontFamily:
                    "var(--font-serif), 'Instrument Serif', Georgia, serif",
                  color: INK,
                  lineHeight: 1.2,
                  marginTop: 2,
                  fontWeight: 400,
                }}
              >
                {handle}, you've used your 5 free searches
              </DrawerPrimitive.Title>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => handleClose(false)}
              style={{
                width: 44,
                height: 44,
                background: "transparent",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                padding: 0,
                margin: "-6px -6px 0 0",
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: "#F1EFE8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="ti ti-x"
                  style={{ fontSize: 14, color: BODY }}
                />
              </span>
            </button>
          </div>
          <DrawerPrimitive.Description className="sr-only">
            Sign up to continue searching.
          </DrawerPrimitive.Description>

          <div style={{ padding: "8px 20px 4px" }}>
            <p
              style={{
                fontSize: 16,
                fontWeight: 400,
                color: BODY,
                margin: 0,
                lineHeight: 1.4,
              }}
            >
              Sign up free to keep going
            </p>

            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: "16px 0 20px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {[
                "Save outfits you love",
                "Build your wardrobe for tailored recommendations",
                "Get personalised styling for occasions and seasons",
              ].map((b) => (
                <li
                  key={b}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    fontSize: 14,
                    color: BODY,
                    lineHeight: 1.4,
                  }}
                >
                  <i
                    className="ti ti-check"
                    style={{ fontSize: 16, color: CORAL, marginTop: 2, flexShrink: 0 }}
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {!isAndroid() && (
                <button
                  type="button"
                  onClick={handleApple}
                  disabled={busy !== null}
                  style={{
                    width: "100%",
                    minHeight: 44,
                    padding: 14,
                    borderRadius: 999,
                    background: CORAL_BG,
                    color: "#FFFFFF",
                    border: "none",
                    fontSize: 15,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    cursor: "pointer",
                    opacity: busy !== null ? 0.6 : 1,
                  }}
                >
                  <i className="ti ti-brand-apple-filled" style={{ fontSize: 18 }} />
                  Continue with Apple
                </button>
              )}
              <button
                type="button"
                onClick={handleGoogle}
                disabled={busy !== null}
                style={{
                  width: "100%",
                  minHeight: 44,
                  padding: 14,
                  borderRadius: 999,
                  background: CORAL_BG,
                  color: "#FFFFFF",
                  border: "none",
                  fontSize: 15,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  cursor: "pointer",
                  opacity: busy !== null ? 0.6 : 1,
                }}
              >
                <i className="ti ti-brand-google" style={{ fontSize: 18 }} />
                Continue with Google
              </button>
              <button
                type="button"
                onClick={handleEmail}
                disabled={busy !== null}
                style={{
                  width: "100%",
                  minHeight: 44,
                  padding: 14,
                  borderRadius: 999,
                  background: "transparent",
                  color: CORAL_BG,
                  border: `1px solid ${CORAL_BG}`,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: busy !== null ? 0.6 : 1,
                }}
              >
                Continue with email
              </button>
            </div>

            {error && (
              <p
                style={{
                  marginTop: 12,
                  fontSize: 13,
                  color: CORAL,
                  textAlign: "center",
                }}
              >
                {error}
              </p>
            )}
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

/** Fires guest_paywall_shown exactly once each time the sheet opens. */
function useShownGuard(open: boolean, guestHandle: string | null, searchCount: number) {
  useEffect(() => {
    if (!open) return;
    trackEvent("guest_paywall_shown", {
      guest_handle: guestHandle,
      search_count: searchCount,
    });
  }, [open, guestHandle, searchCount]);
}
